import { POD_NAMES, PRIORITIES, PRIORITY_CONFIG, PULSE_COLORS } from './pulseConfig.js'
import { podNameToKey } from './pulseHelpers.js'

function getDisplayQaFailedPods(byPod = [], selectedPod) {
    if (selectedPod === 'all') return byPod
    const podKey = podNameToKey(selectedPod)
    return podKey ? byPod.filter((p) => p.podKey === podKey) : byPod
}

function StackedSplitBar({ withAiCompleted, withoutAiCompleted }) {
    const total = withAiCompleted + withoutAiCompleted
    const withAiPct = total === 0 ? 0 : (withAiCompleted / total) * 100
    const withoutAiPct = total === 0 ? 0 : 100 - withAiPct

    return (
        <div
            style={{
                display: 'flex',
                height: '8px',
                minWidth: '96px',
                overflow: 'hidden',
                borderRadius: '999px',
                background: PULSE_COLORS.bgInner,
            }}
            aria-label={`${withAiCompleted} AICompleted and ${withoutAiCompleted} Not AICompleted`}
        >
            {withAiCompleted > 0 && (
                <div
                    style={{ width: `${withAiPct}%`, background: '#F43F5E' }}
                    title={`${withAiCompleted} AICompleted`}
                />
            )}
            {withoutAiCompleted > 0 && (
                <div
                    style={{ width: `${withoutAiPct}%`, background: PULSE_COLORS.textSubtle }}
                    title={`${withoutAiCompleted} Not AICompleted`}
                />
            )}
        </div>
    )
}

export default function QaFailedMetrics({ qaFailed, selectedPod, headerAction }) {
    const safeQaFailed = qaFailed ?? {
        total: 0,
        byPriority: { P0: 0, P1: 0, P2: 0, P3: 0 },
        withAiCompleted: 0,
        withoutAiCompleted: 0,
        byPod: [],
    }
    const displayPods = getDisplayQaFailedPods(safeQaFailed.byPod, selectedPod)
    const displayTotal =
        selectedPod === 'all' ? safeQaFailed.total : displayPods.reduce((sum, pod) => sum + pod.total, 0)

    return (
        <div
            style={{
                height: '100%',
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #7F1D1D',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '16px',
                    marginBottom: '16px',
                }}
            >
                <div>
                    <h3 style={{ color: '#FDA4AF', fontWeight: 600, fontSize: '16px', margin: 0 }}>QA Failed</h3>
                    <p style={{ color: '#FDA4AF', opacity: 0.85, fontSize: '13px', margin: '6px 0 0' }}>
                        Issues resolved as QA Fail in the selected range.
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#FDA4AF', fontSize: '30px', fontWeight: 700 }}>{displayTotal}</div>
                        <div
                            style={{
                                color: PULSE_COLORS.textMuted,
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                            }}
                        >
                            Total
                        </div>
                    </div>
                    {headerAction}
                </div>
            </div>

            {displayPods.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <p
                        style={{
                            color: '#FDA4AF',
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            margin: '0 0 8px',
                        }}
                    >
                        Pod Details
                    </p>
                    <table style={{ width: '100%', minWidth: '760px', fontSize: '13px', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #7F1D1D' }}>
                                <HeaderCell align="left">Pod</HeaderCell>
                                <HeaderCell>QA Fail</HeaderCell>
                                <HeaderCell color="#FDA4AF">AICompleted</HeaderCell>
                                <HeaderCell color={PULSE_COLORS.textMuted}>Not AICompleted</HeaderCell>
                                <HeaderCell align="left">Split</HeaderCell>
                                {PRIORITIES.map((p) => (
                                    <HeaderCell key={p} color={PRIORITY_CONFIG[p].textColor}>
                                        {p}
                                    </HeaderCell>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {displayPods.map((pod) => (
                                <tr key={pod.podKey} style={{ borderBottom: '1px solid #7F1D1D66' }}>
                                    <Cell align="left" strong>
                                        {POD_NAMES[pod.podKey] ?? pod.podKey}
                                    </Cell>
                                    <Cell strong>{pod.total}</Cell>
                                    <Cell color="#FDA4AF" strong>
                                        {pod.withAiCompleted}
                                    </Cell>
                                    <Cell color={PULSE_COLORS.textMuted} strong>
                                        {pod.withoutAiCompleted}
                                    </Cell>
                                    <Cell align="left">
                                        <StackedSplitBar
                                            withAiCompleted={pod.withAiCompleted}
                                            withoutAiCompleted={pod.withoutAiCompleted}
                                        />
                                    </Cell>
                                    {PRIORITIES.map((p) => (
                                        <Cell
                                            key={`${pod.podKey}-${p}`}
                                            color={
                                                pod.priorities[p] > 0
                                                    ? PRIORITY_CONFIG[p].textColor
                                                    : PULSE_COLORS.textSubtle
                                            }
                                            strong
                                        >
                                            {pod.priorities[p]}
                                        </Cell>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

function HeaderCell({ children, color = PULSE_COLORS.textMuted, align = 'center' }) {
    return (
        <th
            style={{
                textAlign: align,
                padding: align === 'left' ? '8px 16px 8px 0' : '8px 12px',
                color,
                fontWeight: 600,
                fontSize: '12px',
            }}
        >
            {children}
        </th>
    )
}

function Cell({ children, color = PULSE_COLORS.text, align = 'center', strong = false }) {
    return (
        <td
            style={{
                textAlign: align,
                padding: align === 'left' ? '8px 16px 8px 0' : '8px 12px',
                color,
                fontWeight: strong ? 600 : 400,
            }}
        >
            {children}
        </td>
    )
}
